import { MedplumClient } from "@medplum/core";
import { HealthcareService } from "@medplum/fhirtypes";

import camelCase from "lodash/camelCase";

import { Systems } from "@/fhir/enums/systems";
import { joinReferenceString, joinTokenString } from "@/utils/medplum";

/**
 * Returns location-hours HealthcareService resources that have configured availability.
 *
 * @param locationHoursServices Location-hours services returned from FHIR search.
 * @returns Services with at least one availableTime entry.
 */
export function filterLocationHoursWithAvailability(
  locationHoursServices: HealthcareService[],
): HealthcareService[] {
  return locationHoursServices.filter(
    (locationHoursService) =>
      locationHoursService.availableTime && locationHoursService.availableTime.length > 0,
  );
}

/**
 * Fetches location-hours HealthcareService resources for an organization.
 *
 * Uses the same FHIR search as the Location Hours settings tab.
 *
 * @param medplumClient Medplum client used for FHIR search.
 * @param organizationId Practice organization id.
 * @returns Location-hours services with configured availability.
 */
export async function fetchOrganizationLocationHoursWithAvailability(
  medplumClient: MedplumClient,
  organizationId: string,
): Promise<HealthcareService[]> {
  const locationHoursServices = await medplumClient.searchResources("HealthcareService", {
    organization: joinReferenceString("Organization", organizationId),
    "service-category": joinTokenString(Systems.QH, camelCase("Location Hours")),
    _revinclude: "HealthcareService:location",
  });

  return filterLocationHoursWithAvailability(locationHoursServices);
}
